<template>
  <div class="cdoc-workspace" @keydown.capture="handleEditorShortcut">
    <div class="cdoc-header">
      <div>
        <strong>{{ title }}</strong>
        <span>{{ annotationMode ? 'Clique na página para adicionar um comentário.' : helper }}</span>
      </div>
      <div class="cdoc-status">
        <span class="cdoc-status-pill">{{ wordCount }} palavras</span>
        <span class="cdoc-status-pill">{{ localComments.length }} comentário(s)</span>
      </div>
      <div v-if="reviewMarkers.length" class="cdoc-review-legend">
        <span v-for="marker in reviewMarkers" :key="marker.type" :class="`cdoc-review-legend__item cdoc-review-legend__item--${marker.type}`">
          {{ marker.label }}
        </span>
      </div>
    </div>

    <div class="cdoc-toolbar">
      <v-btn-toggle dense>
        <v-btn small :disabled="readonly || !canUndo" title="Desfazer (Ctrl+Z)" @click="undoHistory">
          <v-icon small>mdi-undo</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly || !canRedo" title="Refazer (Ctrl+Y)" @click="redoHistory">
          <v-icon small>mdi-redo</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-select
        v-model="editorFont"
        :disabled="readonly"
        :items="fontOptions"
        dense
        outlined
        hide-details
        label="Fonte"
        class="cdoc-toolbar-select"
        @mousedown.native="beginHistoryChange"
        @focus.native="beginHistoryChange"
        @change="applyFont"
      />
      <v-select
        v-model="editorBlock"
        :disabled="readonly"
        :items="blockOptions"
        dense
        outlined
        hide-details
        label="Estilo"
        class="cdoc-toolbar-select cdoc-toolbar-select--small"
        @mousedown.native="beginHistoryChange"
        @focus.native="beginHistoryChange"
        @change="applyBlock"
      />
      <v-select
        v-model="editorLineHeight"
        :disabled="readonly"
        :items="lineHeightOptions"
        dense
        outlined
        hide-details
        label="Entrelinha"
        class="cdoc-toolbar-select cdoc-toolbar-select--tiny"
        @mousedown.native="beginHistoryChange"
        @focus.native="beginHistoryChange"
        @change="applyLayoutStyle"
      />
      <v-btn-toggle dense mandatory>
        <v-btn small :disabled="readonly" title="Diminuir texto" @click="adjustFontSize(-1)">
          <v-icon small>mdi-format-font-size-decrease</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Aumentar texto" @click="adjustFontSize(1)">
          <v-icon small>mdi-format-font-size-increase</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-btn-toggle dense>
        <v-btn small :disabled="readonly" title="Negrito" @click="formatSelection('bold')">
          <v-icon small>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Itálico" @click="formatSelection('italic')">
          <v-icon small>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Sublinhado" @click="formatSelection('underline')">
          <v-icon small>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Realçar" @click="highlightSelection">
          <v-icon small>mdi-format-color-highlight</v-icon>
        </v-btn>
      </v-btn-toggle>
      <v-btn-toggle dense>
        <v-btn small :disabled="readonly" title="Lista" @click="formatSelection('insertUnorderedList')">
          <v-icon small>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Alinhar à esquerda" @click="setAlignment('left', 'justifyLeft')">
          <v-icon small>mdi-format-align-left</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Centralizar" @click="setAlignment('center', 'justifyCenter')">
          <v-icon small>mdi-format-align-center</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Alinhar à direita" @click="setAlignment('right', 'justifyRight')">
          <v-icon small>mdi-format-align-right</v-icon>
        </v-btn>
        <v-btn small :disabled="readonly" title="Justificar" @click="setAlignment('justify', 'justifyFull')">
          <v-icon small>mdi-format-align-justify</v-icon>
        </v-btn>
      </v-btn-toggle>
      <label class="cdoc-color-control" title="Cor do texto">
        <v-icon small>mdi-format-color-text</v-icon>
        <input v-model="editorTextColor" :disabled="readonly" type="color" @focus="beginHistoryChange" @pointerdown="beginHistoryChange" @input="applyTextColor">
      </label>
      <label class="cdoc-color-control" title="Cor de realce">
        <v-icon small>mdi-format-color-highlight</v-icon>
        <input v-model="editorHighlightColor" :disabled="readonly" type="color" @focus="beginHistoryChange" @pointerdown="beginHistoryChange" @input="applyLayoutStyle">
      </label>
      <v-btn
        :outlined="!layoutPanelOpen"
        color="primary"
        small
        @click="layoutPanelOpen = !layoutPanelOpen"
      >
        <v-icon left small>mdi-page-layout-header-footer</v-icon>
        Layout
      </v-btn>
      <v-btn
        :outlined="!annotationMode"
        :disabled="!canComment"
        color="primary"
        small
        @click="toggleAnnotationMode"
      >
        <v-icon left small>{{ annotationMode ? 'mdi-comment-text-outline' : 'mdi-comment-plus-outline' }}</v-icon>
        {{ annotationMode ? 'Comentando' : 'Comentar' }}
      </v-btn>
    </div>

    <div v-if="layoutPanelOpen" class="cdoc-layout-panel">
      <div class="cdoc-layout-panel__header">
        <strong>Layout do documento</strong>
        <span>Capa, cabeçalho, rodapé e marca d'água</span>
      </div>
      <div class="cdoc-layout-grid">
        <v-checkbox v-model="draftLayout.cover.enabled" :disabled="readonly" dense hide-details label="Capa" @mousedown.native="beginHistoryChange" @change="emitLayout" />
        <v-checkbox v-model="draftLayout.header.enabled" :disabled="readonly" dense hide-details label="Cabeçalho" @mousedown.native="beginHistoryChange" @change="emitLayout" />
        <v-checkbox v-model="draftLayout.footer.enabled" :disabled="readonly" dense hide-details label="Rodapé" @mousedown.native="beginHistoryChange" @change="emitLayout" />
        <v-checkbox v-model="draftLayout.watermark.enabled" :disabled="readonly" dense hide-details label="Marca d'água" @mousedown.native="beginHistoryChange" @change="emitLayout" />
        <v-text-field v-model="draftLayout.cover.title" :disabled="readonly || !draftLayout.cover.enabled" dense outlined hide-details label="Título da capa" @focus.native="beginHistoryChange" @input="emitLayout" />
        <v-text-field v-model="draftLayout.header.text" :disabled="readonly || !draftLayout.header.enabled" dense outlined hide-details label="Texto do cabeçalho" @focus.native="beginHistoryChange" @input="emitLayout" />
        <v-text-field v-model="draftLayout.footer.text" :disabled="readonly || !draftLayout.footer.enabled" dense outlined hide-details label="Texto do rodapé" @focus.native="beginHistoryChange" @input="emitLayout" />
        <v-text-field v-model="draftLayout.watermark.text" :disabled="readonly || !draftLayout.watermark.enabled" dense outlined hide-details label="Texto da marca d'água" @focus.native="beginHistoryChange" @input="emitLayout" />
      </div>
    </div>

    <div class="cdoc-shell" :class="{ 'cdoc-shell--comments': showCommentsPanel }">
      <div
        class="cdoc-stage"
        :class="{ 'cdoc-stage--annotating': annotationMode }"
        @click="handlePageClick"
      >
        <section v-if="draftLayout.cover.enabled" class="cdoc-cover">
          <span>{{ draftLayout.cover.subtitle || 'Documento' }}</span>
          <h1>{{ draftLayout.cover.title || fallbackCoverTitle }}</h1>
          <p>{{ draftLayout.cover.client_name }}</p>
          <small>{{ draftLayout.cover.matter_reference }}</small>
        </section>
        <div class="cdoc-page" :class="{ 'cdoc-page--annotating': annotationMode }">
          <div
            v-if="floatingToolbar.visible && !readonly"
            ref="floatingToolbar"
            class="cdoc-floating-toolbar"
            :style="{ left: `${floatingToolbar.x}px`, top: `${floatingToolbar.y}px` }"
            @mousedown.stop
          >
            <button type="button" title="Negrito" @mousedown.prevent @click="formatSelection('bold')">
              <v-icon small>mdi-format-bold</v-icon>
            </button>
            <button type="button" title="Itálico" @mousedown.prevent @click="formatSelection('italic')">
              <v-icon small>mdi-format-italic</v-icon>
            </button>
            <button type="button" title="Sublinhado" @mousedown.prevent @click="formatSelection('underline')">
              <v-icon small>mdi-format-underline</v-icon>
            </button>
            <span class="cdoc-floating-toolbar__divider" />
            <div class="cdoc-floating-select">
              <button type="button" class="cdoc-floating-select__trigger" title="Fonte" @mousedown.prevent @click.stop="toggleFloatingMenu('font')">
                <span>{{ editorFont }}</span>
                <v-icon small>mdi-chevron-down</v-icon>
              </button>
              <div v-if="floatingMenus.font" class="cdoc-floating-select__menu cdoc-floating-select__menu--fonts">
                <button
                  v-for="font in fontOptions"
                  :key="font"
                  type="button"
                  :class="{ active: editorFont === font }"
                  :style="{ fontFamily: `${font}, Georgia, serif` }"
                  @mousedown.prevent
                  @click.stop="chooseFloatingFont(font)"
                >
                  {{ font }}
                </button>
              </div>
            </div>
            <div class="cdoc-floating-select cdoc-floating-select--style">
              <button type="button" class="cdoc-floating-select__trigger" title="Estilo" @mousedown.prevent @click.stop="toggleFloatingMenu('style')">
                <span>{{ selectedBlockLabel }}</span>
                <v-icon small>mdi-chevron-down</v-icon>
              </button>
              <div v-if="floatingMenus.style" class="cdoc-floating-select__menu">
                <button
                  v-for="block in blockOptions"
                  :key="block.value"
                  type="button"
                  :class="{ active: editorBlock === block.value }"
                  @mousedown.prevent
                  @click.stop="chooseFloatingBlock(block.value)"
                >
                  {{ block.text }}
                </button>
              </div>
            </div>
            <label title="Cor do texto">
              <v-icon small>mdi-format-color-text</v-icon>
              <input v-model="editorTextColor" type="color" @focus="beginHistoryChange" @pointerdown="beginHistoryChange" @input="applyFloatingTextColor">
            </label>
            <label title="Cor de realce">
              <v-icon small>mdi-format-color-highlight</v-icon>
              <input v-model="editorHighlightColor" type="color" @focus="beginHistoryChange" @pointerdown="beginHistoryChange" @input="applyFloatingHighlightColor">
            </label>
            <button v-if="canComment" type="button" title="Comentar trecho" @mousedown.prevent @click="startCommentFromSelection">
              <v-icon small>mdi-comment-plus-outline</v-icon>
            </button>
          </div>

          <div v-if="draftLayout.watermark.enabled && draftLayout.watermark.text" class="cdoc-watermark">
            {{ draftLayout.watermark.text }}
          </div>
          <header v-if="draftLayout.header.enabled && draftLayout.header.text" class="cdoc-page-header">
            {{ draftLayout.header.text }}
          </header>
          <article
            ref="editorPage"
            class="cdoc-editable-page"
            :contenteditable="!readonly"
            spellcheck="true"
            @input="syncEditorText"
            @keyup="updateFloatingToolbar"
            @mouseup="updateFloatingToolbar"
            @dblclick.stop="handleEditorComment"
          />
          <footer v-if="draftLayout.footer.enabled && draftLayout.footer.text" class="cdoc-page-footer">
            {{ draftLayout.footer.text }}
          </footer>
          <button
            v-for="(comment, index) in localComments"
            :key="comment.id"
            type="button"
            class="cdoc-comment-marker"
            :class="{ active: selectedCommentId === comment.id }"
            :style="{ left: `${comment.x}%`, top: `${comment.y}%` }"
            @click.stop="selectComment(comment)"
          >
            {{ index + 1 }}
          </button>
          <div
            v-if="commentDraft.visible"
            class="cdoc-comment-composer"
            :style="{ left: `${commentDraft.x}%`, top: `${commentDraft.y}%` }"
            @mousedown.stop
            @click.stop
          >
            <div class="cdoc-comment-composer__header">
              <strong>Novo comentário</strong>
              <button type="button" aria-label="Fechar comentário" class="cdoc-comment-composer__close" @click="cancelComment">
                <v-icon small>mdi-close</v-icon>
              </button>
            </div>
            <textarea ref="commentComposerField" v-model="commentDraft.text" placeholder="Escreva um comentário sobre este trecho" />
            <div class="cdoc-comment-composer__actions">
              <button type="button" @click="cancelComment">Cancelar</button>
              <button type="button" class="primary" @click="saveComment">Salvar</button>
            </div>
          </div>
        </div>
      </div>

      <aside v-if="showCommentsPanel" class="cdoc-comments-panel">
        <h3>Comentários</h3>
        <p>Clique no documento para registrar apontamentos.</p>
        <article
          v-for="(comment, index) in localComments"
          :key="comment.id"
          class="cdoc-comment-card"
          :class="{ active: selectedCommentId === comment.id }"
          @click="selectComment(comment)"
        >
          <strong>#{{ index + 1 }}</strong>
          <p>{{ comment.text }}</p>
          <v-btn v-if="canComment" x-small text color="error" @click.stop="removeComment(comment)">Remover</v-btn>
        </article>
        <div v-if="!localComments.length" class="cdoc-empty-comments">Nenhum comentário registrado.</div>
      </aside>
    </div>
  </div>
</template>

<script>
const defaultLayout = () => ({
  font_family: 'Georgia',
  font_size: 15,
  line_height: 1.65,
  text_align: 'left',
  text_color: '#17211f',
  highlight_color: '#fff0b3',
  cover: { enabled: false, title: '', subtitle: '', client_name: '', matter_reference: '', author: '', date: '' },
  header: { enabled: false, text: '' },
  footer: { enabled: false, text: '', show_page_number: false },
  watermark: { enabled: false, text: '', opacity: 0.08 },
  logo: { enabled: false, url: '' }
})

export default {
  name: 'DocumentEditorWorkspace',
  props: {
    value: { type: String, default: '' },
    comments: { type: Array, default: () => [] },
    layoutSettings: { type: Object, default: () => defaultLayout() },
    title: { type: String, default: 'Editor da minuta' },
    helper: { type: String, default: 'Edite texto, fonte e estilos antes de validar ou enviar para revisão.' },
    contentHtml: { type: String, default: '' },
    reviewMarkers: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    canComment: { type: Boolean, default: true },
    coverTitle: { type: String, default: '' }
  },
  data () {
    const layout = this.mergeLayout(this.layoutSettings)
    return {
      annotationMode: false,
      selectedCommentId: '',
      commentDraft: { visible: false, x: 50, y: 20, text: '' },
      localComments: [...this.comments],
      draftLayout: layout,
      layoutPanelOpen: false,
      editorFont: layout.font_family,
      editorBlock: 'P',
      editorBaseSize: Number(layout.font_size || 15),
      editorLineHeight: Number(layout.line_height || 1.65),
      editorTextColor: layout.text_color || '#17211f',
      editorHighlightColor: layout.highlight_color || '#fff0b3',
      historyStack: [],
      historyIndex: -1,
      historyTimer: null,
      pendingHistorySnapshot: null,
      isApplyingHistory: false,
      savedSelectionRange: null,
      floatingToolbar: { visible: false, x: 0, y: 0 },
      floatingMenus: { font: false, style: false },
      fontOptions: [
        'Georgia',
        'Montserrat',
        'Poppins',
        'Raleway',
        'Lato',
        'Open Sans',
        'Source Sans 3',
        'Nunito Sans',
        'Work Sans',
        'Merriweather',
        'Libre Baskerville',
        'Roboto Slab',
        'Times New Roman',
        'Verdana',
        'Courier New',
        'Calibri',
        'Cambria'
      ],
      blockOptions: [
        { text: 'Corpo', value: 'P' },
        { text: 'Título', value: 'H2' },
        { text: 'Subtítulo', value: 'H3' },
        { text: 'Citação', value: 'BLOCKQUOTE' }
      ],
      lineHeightOptions: [
        { text: '1.25', value: 1.25 },
        { text: '1.5', value: 1.5 },
        { text: '1.65', value: 1.65 },
        { text: '2.0', value: 2 }
      ]
    }
  },
  computed: {
    wordCount () {
      return String(this.value || '').trim().split(/\s+/).filter(Boolean).length
    },
    fallbackCoverTitle () {
      return this.coverTitle || 'Documento'
    },
    canUndo () {
      return this.historyIndex > 0
    },
    canRedo () {
      return this.historyIndex >= 0 && this.historyIndex < this.historyStack.length - 1
    },
    selectedBlockLabel () {
      const block = this.blockOptions.find(item => item.value === this.editorBlock)
      return block ? block.text : 'Corpo'
    },
    showCommentsPanel () {
      return !this.commentDraft.visible && (this.annotationMode || this.localComments.length)
    }
  },
  watch: {
    value () {
      this.renderEditorText()
    },
    contentHtml () {
      this.renderEditorText()
    },
    comments: {
      deep: true,
      handler (comments) {
        this.localComments = [...(comments || [])]
      }
    },
    layoutSettings: {
      deep: true,
      handler (settings) {
        const layout = this.mergeLayout(settings)
        this.draftLayout = layout
        this.editorFont = layout.font_family
        this.editorBaseSize = Number(layout.font_size || 15)
        this.editorLineHeight = Number(layout.line_height || 1.65)
        this.editorTextColor = layout.text_color || '#17211f'
        this.editorHighlightColor = layout.highlight_color || '#fff0b3'
        this.$nextTick(() => this.applyEditorBaseStyle())
      }
    }
  },
  mounted () {
    this.renderEditorText()
    this.$nextTick(() => this.pushHistorySnapshot(true))
    window.addEventListener('keydown', this.handleEditorShortcut, true)
    document.addEventListener('selectionchange', this.handleDocumentSelectionChange)
    window.addEventListener('resize', this.hideFloatingToolbar)
    window.addEventListener('scroll', this.handleWindowScroll, true)
  },
  beforeDestroy () {
    if (this.historyTimer) window.clearTimeout(this.historyTimer)
    window.removeEventListener('keydown', this.handleEditorShortcut, true)
    document.removeEventListener('selectionchange', this.handleDocumentSelectionChange)
    window.removeEventListener('resize', this.hideFloatingToolbar)
    window.removeEventListener('scroll', this.handleWindowScroll, true)
  },
  methods: {
    mergeLayout (settings) {
      const defaults = defaultLayout()
      const value = settings || {}
      return {
        ...defaults,
        ...value,
        cover: { ...defaults.cover, ...(value.cover || {}) },
        header: { ...defaults.header, ...(value.header || {}) },
        footer: { ...defaults.footer, ...(value.footer || {}) },
        watermark: { ...defaults.watermark, ...(value.watermark || {}) },
        logo: { ...defaults.logo, ...(value.logo || {}) }
      }
    },
    renderEditorText () {
      this.$nextTick(() => {
        const page = this.$refs.editorPage
        if (!page) return
        const current = page.innerText
        if (current === String(this.value || '') && page.innerHTML) {
          this.applyEditorBaseStyle()
          return
        }
        page.innerHTML = this.contentHtml || this.escapeHtml(this.value || '')
        this.applyEditorBaseStyle()
      })
    },
    syncEditorText () {
      if (!this.$refs.editorPage) return
      this.$emit('input', this.$refs.editorPage.innerText)
      this.scheduleHistorySnapshot(350)
    },
    focusEditor () {
      if (this.$refs.editorPage) this.$refs.editorPage.focus()
    },
    formatSelection (command, value = null) {
      if (this.readonly) return
      this.beginHistoryChange()
      this.focusEditor()
      this.restoreSavedSelection()
      document.execCommand(command, false, value)
      this.syncEditorText()
      this.$nextTick(() => this.pushHistorySnapshot())
    },
    applyFont () {
      const hasSelection = this.hasSavedTextSelection()
      this.beginControlHistoryChange()
      if (hasSelection) {
        this.formatSelection('fontName', this.editorFont)
        this.$nextTick(() => this.pushHistorySnapshot())
      } else {
        this.draftLayout.font_family = this.editorFont
        this.applyLayoutStyle()
      }
    },
    applyBlock () {
      this.formatSelection('formatBlock', this.editorBlock)
    },
    adjustFontSize (step) {
      if (this.readonly) return
      this.beginHistoryChange()
      this.editorBaseSize = Math.max(12, Math.min(28, this.editorBaseSize + step))
      this.applyLayoutStyle()
    },
    applyTextColor () {
      const hasSelection = this.hasSavedTextSelection()
      this.beginControlHistoryChange()
      if (hasSelection) {
        this.formatSelection('foreColor', this.editorTextColor)
        this.$nextTick(() => this.pushHistorySnapshot())
      } else {
        this.draftLayout.text_color = this.editorTextColor
        this.applyLayoutStyle()
      }
    },
    highlightSelection () {
      if (this.readonly) return
      this.beginControlHistoryChange()
      this.focusEditor()
      this.restoreSavedSelection()
      document.execCommand('backColor', false, this.editorHighlightColor)
      this.syncEditorText()
      this.$nextTick(() => this.pushHistorySnapshot())
    },
    applyFloatingTextColor () {
      this.applyFloatingFormat('foreColor', this.editorTextColor)
    },
    applyFloatingHighlightColor () {
      this.applyFloatingFormat('backColor', this.editorHighlightColor)
    },
    applyFloatingFormat (command, value) {
      if (this.readonly || !this.hasSavedTextSelection()) return
      const page = this.$refs.editorPage
      if (!page) return
      const beforeEntry = this.createHistoryEntry()
      const beforeHtml = page.innerHTML
      this.focusEditor()
      this.restoreSavedSelection()
      document.execCommand(command, false, value)
      const afterHtml = page.innerHTML
      if (beforeHtml !== afterHtml) this.commitImmediateHistoryChange(beforeEntry)
      this.$emit('input', page.innerText)
      this.$nextTick(() => {
        this.updateFloatingToolbar()
      })
    },
    setAlignment (align, command) {
      if (this.readonly) return
      this.beginHistoryChange()
      this.draftLayout.text_align = align
      this.formatSelection(command)
      this.applyLayoutStyle()
    },
    applyLayoutStyle () {
      this.beginControlHistoryChange()
      this.draftLayout.font_family = this.editorFont
      this.draftLayout.font_size = this.editorBaseSize
      this.draftLayout.line_height = this.editorLineHeight
      this.draftLayout.text_color = this.editorTextColor
      this.draftLayout.highlight_color = this.editorHighlightColor
      this.applyEditorBaseStyle()
      this.emitLayout()
    },
    applyEditorBaseStyle () {
      if (!this.$refs.editorPage) return
      this.$refs.editorPage.style.fontFamily = `${this.editorFont}, Georgia, "Times New Roman", serif`
      this.$refs.editorPage.style.fontSize = `${this.editorBaseSize}px`
      this.$refs.editorPage.style.lineHeight = this.editorLineHeight
      this.$refs.editorPage.style.color = this.editorTextColor
      this.$refs.editorPage.style.textAlign = this.draftLayout.text_align || 'left'
    },
    emitLayout () {
      this.$emit('update:layout-settings', this.mergeLayout(this.draftLayout))
      this.scheduleHistorySnapshot(150)
    },
    toggleAnnotationMode () {
      if (!this.canComment) return
      this.annotationMode = !this.annotationMode
      this.selectedCommentId = ''
      this.cancelComment()
    },
    handlePageClick (event) {
      if (!this.annotationMode || !this.canComment) return
      if (event.target && event.target.closest && (
        event.target.closest('.cdoc-comment-composer') ||
        event.target.closest('.cdoc-comment-marker')
      )) return

      const rect = event.currentTarget.getBoundingClientRect()
      this.openCommentComposer(
        ((event.clientX - rect.left) / rect.width) * 100,
        ((event.clientY - rect.top) / rect.height) * 100
      )
    },
    handleEditorComment (event) {
      if (!this.annotationMode || !this.canComment) return
      const wrap = event.currentTarget.parentElement
      if (!wrap) return
      const rect = wrap.getBoundingClientRect()
      this.openCommentComposer(
        ((event.clientX - rect.left) / rect.width) * 100,
        ((event.clientY - rect.top) / rect.height) * 100
      )
    },
    openCommentComposer (x, y) {
      this.commentDraft = {
        visible: true,
        x: Math.max(20, Math.min(80, x)),
        y: Math.max(5, Math.min(90, y)),
        text: ''
      }
      this.selectedCommentId = ''
      this.$nextTick(() => {
        if (this.$refs.commentComposerField) this.$refs.commentComposerField.focus()
      })
    },
    saveComment () {
      const text = String(this.commentDraft.text || '').trim()
      if (!text) return
      this.localComments = [
        ...this.localComments,
        { id: `comment-${Date.now()}`, x: this.commentDraft.x, y: this.commentDraft.y, text, resolved: false }
      ]
      this.$emit('update:comments', this.localComments)
      this.cancelComment()
      this.pushHistorySnapshot()
    },
    cancelComment () {
      this.commentDraft = { visible: false, x: 50, y: 20, text: '' }
    },
    selectComment (comment) {
      this.selectedCommentId = comment.id
    },
    removeComment (comment) {
      this.localComments = this.localComments.filter(item => item.id !== comment.id)
      this.$emit('update:comments', this.localComments)
      if (this.selectedCommentId === comment.id) this.selectedCommentId = ''
      this.pushHistorySnapshot()
    },
    handleEditorShortcut (event) {
      if (this.readonly || !(event.ctrlKey || event.metaKey)) return
      const key = String(event.key || '').toLowerCase()
      if (event.target && event.target.tagName === 'TEXTAREA') return

      if (key === 'z' && !event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        this.undoHistory()
        return
      }

      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault()
        event.stopPropagation()
        this.redoHistory()
      }
    },
    handleDocumentSelectionChange () {
      window.requestAnimationFrame(() => this.updateFloatingToolbar())
    },
    handleWindowScroll (event) {
      const toolbar = this.$refs.floatingToolbar
      if (toolbar && event && event.target && toolbar.contains(event.target)) return
      this.hideFloatingToolbar()
    },
    selectionBelongsToEditor (range) {
      const page = this.$refs.editorPage
      if (!page || !range) return false
      const node = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer.parentElement
      return Boolean(node && page.contains(node))
    },
    updateFloatingToolbar () {
      if (this.readonly || this.annotationMode) {
        this.hideFloatingToolbar()
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed || !String(selection.toString()).trim()) {
        this.hideFloatingToolbar()
        return
      }

      const range = selection.getRangeAt(0)
      if (!this.selectionBelongsToEditor(range)) {
        this.hideFloatingToolbar()
        return
      }

      const rect = range.getBoundingClientRect()
      const fallbackRect = range.getClientRects && range.getClientRects().length ? range.getClientRects()[0] : rect
      if (!fallbackRect || (!fallbackRect.width && !fallbackRect.height)) {
        this.hideFloatingToolbar()
        return
      }

      this.savedSelectionRange = range.cloneRange()
      const toolbarWidth = 520
      const left = Math.max(12, Math.min(fallbackRect.left + (fallbackRect.width / 2) - (toolbarWidth / 2), window.innerWidth - toolbarWidth - 12))
      const top = fallbackRect.top > 70 ? fallbackRect.top - 58 : fallbackRect.bottom + 12
      this.floatingToolbar = {
        visible: true,
        x: Math.round(left),
        y: Math.round(Math.max(12, top))
      }
    },
    hideFloatingToolbar () {
      this.floatingToolbar = { ...this.floatingToolbar, visible: false }
      this.closeFloatingMenus()
    },
    toggleFloatingMenu (menu) {
      this.restoreSavedSelection()
      this.floatingMenus = {
        font: menu === 'font' ? !this.floatingMenus.font : false,
        style: menu === 'style' ? !this.floatingMenus.style : false
      }
    },
    closeFloatingMenus () {
      this.floatingMenus = { font: false, style: false }
    },
    chooseFloatingFont (font) {
      this.editorFont = font
      this.closeFloatingMenus()
      this.applyFloatingFormat('fontName', this.editorFont)
    },
    chooseFloatingBlock (block) {
      this.editorBlock = block
      this.closeFloatingMenus()
      this.applyFloatingFormat('formatBlock', this.editorBlock)
    },
    restoreSavedSelection () {
      if (!this.savedSelectionRange) return
      const selection = window.getSelection()
      if (!selection) return
      selection.removeAllRanges()
      selection.addRange(this.savedSelectionRange)
    },
    hasSavedTextSelection () {
      return Boolean(
        this.savedSelectionRange &&
        this.selectionBelongsToEditor(this.savedSelectionRange) &&
        String(this.savedSelectionRange.toString() || '').trim()
      )
    },
    startCommentFromSelection () {
      if (!this.canComment || !this.savedSelectionRange) return
      this.restoreSavedSelection()
      const rect = this.savedSelectionRange.getBoundingClientRect()
      const page = this.$refs.editorPage
      const wrap = page ? page.parentElement : null
      if (!wrap) return
      const wrapRect = wrap.getBoundingClientRect()
      this.annotationMode = false
      this.openCommentComposer(
        ((rect.left + rect.width / 2 - wrapRect.left) / wrapRect.width) * 100,
        ((rect.bottom - wrapRect.top) / wrapRect.height) * 100
      )
      this.hideFloatingToolbar()
    },
    beginHistoryChange () {
      if (this.readonly || this.isApplyingHistory) return
      if (!this.$refs.editorPage) return
      if (this.pendingHistorySnapshot) return
      this.pendingHistorySnapshot = this.createHistoryEntry()
    },
    beginControlHistoryChange () {
      if (this.readonly || this.isApplyingHistory) return
      if (this.pendingHistorySnapshot) return
      const currentEntry = this.historyStack[this.historyIndex]
      if (currentEntry) {
        this.pendingHistorySnapshot = {
          snapshot: JSON.parse(JSON.stringify(currentEntry.snapshot)),
          serialized: currentEntry.serialized
        }
        return
      }

      this.beginHistoryChange()
    },
    createHistorySnapshot () {
      const page = this.$refs.editorPage
      return {
        text: page ? page.innerText : String(this.value || ''),
        html: page ? page.innerHTML : this.escapeHtml(this.value || ''),
        comments: JSON.parse(JSON.stringify(this.localComments || [])),
        layout: JSON.parse(JSON.stringify(this.mergeLayout(this.draftLayout))),
        controls: {
          font: this.editorFont,
          block: this.editorBlock,
          size: this.editorBaseSize,
          lineHeight: this.editorLineHeight,
          textColor: this.editorTextColor,
          highlightColor: this.editorHighlightColor
        }
      }
    },
    createHistoryEntry () {
      const snapshot = this.createHistorySnapshot()
      return {
        snapshot,
        serialized: JSON.stringify(snapshot)
      }
    },
    appendHistoryEntry (stack, entry, force = false) {
      const currentEntry = stack[stack.length - 1]
      if (!force && currentEntry && currentEntry.serialized === entry.serialized) return stack
      return [...stack, entry]
    },
    commitImmediateHistoryChange (beforeEntry) {
      if (this.isApplyingHistory) return
      if (this.historyTimer) {
        window.clearTimeout(this.historyTimer)
        this.historyTimer = null
      }

      const afterEntry = this.createHistoryEntry()
      let nextStack = this.historyStack.slice(0, this.historyIndex + 1)
      nextStack = this.appendHistoryEntry(nextStack, beforeEntry)
      nextStack = this.appendHistoryEntry(nextStack, afterEntry)

      if (nextStack.length > 80) nextStack.shift()
      this.historyStack = nextStack
      this.historyIndex = nextStack.length - 1
      this.pendingHistorySnapshot = null
    },
    pushHistorySnapshot (force = false) {
      if (this.isApplyingHistory) return
      if (this.historyTimer) {
        window.clearTimeout(this.historyTimer)
        this.historyTimer = null
      }

      const currentEntry = this.createHistoryEntry()
      let nextStack = this.historyStack.slice(0, this.historyIndex + 1)
      if (this.pendingHistorySnapshot) {
        nextStack = this.appendHistoryEntry(nextStack, this.pendingHistorySnapshot)
        this.pendingHistorySnapshot = null
      }

      nextStack = this.appendHistoryEntry(nextStack, currentEntry, force)

      if (nextStack.length > 80) nextStack.shift()
      this.historyStack = nextStack
      this.historyIndex = nextStack.length - 1
    },
    scheduleHistorySnapshot (delay = 250) {
      if (this.isApplyingHistory) return
      if (this.historyTimer) window.clearTimeout(this.historyTimer)
      this.historyTimer = window.setTimeout(() => {
        this.historyTimer = null
        this.pushHistorySnapshot()
      }, delay)
    },
    undoHistory () {
      if (this.historyTimer) this.pushHistorySnapshot()
      if (!this.canUndo) return
      this.historyIndex -= 1
      this.applyHistorySnapshot(this.historyStack[this.historyIndex].snapshot)
    },
    redoHistory () {
      if (!this.canRedo) return
      this.historyIndex += 1
      this.applyHistorySnapshot(this.historyStack[this.historyIndex].snapshot)
    },
    applyHistorySnapshot (snapshot) {
      if (!snapshot) return
      this.isApplyingHistory = true
      this.draftLayout = this.mergeLayout(snapshot.layout)
      this.localComments = JSON.parse(JSON.stringify(snapshot.comments || []))
      this.editorFont = snapshot.controls && snapshot.controls.font ? snapshot.controls.font : this.draftLayout.font_family
      this.editorBlock = snapshot.controls && snapshot.controls.block ? snapshot.controls.block : 'P'
      this.editorBaseSize = Number(snapshot.controls && snapshot.controls.size ? snapshot.controls.size : this.draftLayout.font_size || 15)
      this.editorLineHeight = Number(snapshot.controls && snapshot.controls.lineHeight ? snapshot.controls.lineHeight : this.draftLayout.line_height || 1.65)
      this.editorTextColor = snapshot.controls && snapshot.controls.textColor ? snapshot.controls.textColor : this.draftLayout.text_color || '#17211f'
      this.editorHighlightColor = snapshot.controls && snapshot.controls.highlightColor ? snapshot.controls.highlightColor : this.draftLayout.highlight_color || '#fff0b3'

      this.$nextTick(() => {
        const page = this.$refs.editorPage
        if (page) page.innerHTML = snapshot.html || this.escapeHtml(snapshot.text || '')
        this.applyEditorBaseStyle()
        this.$emit('input', snapshot.text || '')
        this.$emit('update:comments', this.localComments)
        this.$emit('update:layout-settings', this.mergeLayout(this.draftLayout))
        this.$nextTick(() => {
          this.isApplyingHistory = false
        })
      })
    },
    focusReviewMarker (item, scroll = true) {
      if (!item || !item.id) return
      this.$nextTick(() => {
        const page = this.$refs.editorPage
        if (!page) return
        const marker = page.querySelector(`[data-review-id="${item.id}"]`)
        if (!marker) return
        marker.classList.add('document-review-highlight--focused')
        window.setTimeout(() => marker.classList.remove('document-review-highlight--focused'), 1400)
        if (scroll) marker.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      })
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/\n/g, '<br>')
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Lato:wght@400;700;900&family=Merriweather:wght@400;700;900&family=Montserrat:wght@400;600;700;800&family=Nunito+Sans:wght@400;700;800&family=Open+Sans:wght@400;700;800&family=Poppins:wght@400;600;700;800&family=Raleway:wght@400;700;800&family=Roboto+Slab:wght@400;700&family=Source+Sans+3:wght@400;700;800&family=Work+Sans:wght@400;700;800&display=swap');

.cdoc-workspace {
  display: flex;
  min-width: 0;
  flex-direction: column;
  border: 1px solid #dde5e2;
  background: #eef2f1;
}

.cdoc-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #dde5e2;
  background: #ffffff;
}

.cdoc-header > div:first-child {
  min-width: 0;
}

.cdoc-header > div:first-child strong,
.cdoc-header > div:first-child span {
  display: block;
}

.cdoc-header > div:first-child strong {
  color: #101918;
  font-size: 15px;
  line-height: 1.25;
}

.cdoc-header > div:first-child span {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.35;
}

.cdoc-status {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.cdoc-status-pill {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid #0f4efb;
  border-radius: 4px;
  background: #ffffff;
  color: #0f4efb;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.cdoc-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #dde5e2;
  background: #fbfcfd;
}

.cdoc-toolbar-select {
  flex: 0 0 178px;
  max-width: 178px;
}

.cdoc-toolbar-select--small {
  flex-basis: 132px;
  max-width: 132px;
}

.cdoc-toolbar-select--tiny {
  flex-basis: 118px;
  max-width: 118px;
}

.cdoc-toolbar ::v-deep .v-input {
  flex-grow: 0;
}

.cdoc-toolbar ::v-deep .v-input__slot {
  min-height: 38px !important;
}

.cdoc-toolbar ::v-deep .v-label {
  font-size: 12px;
}

.cdoc-toolbar ::v-deep .v-btn {
  min-width: 40px !important;
}

.cdoc-toolbar ::v-deep .v-btn-toggle {
  flex: 0 0 auto;
}

.cdoc-review-legend {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cdoc-review-legend__item {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  border: 1px solid #dde5e2;
  color: #394452;
  font-size: 11px;
  font-weight: 800;
}

.cdoc-review-legend__item--placeholder {
  border-color: #f5b34d;
  background: #fff7e8;
}

.cdoc-review-legend__item--numeric {
  border-color: #c084fc;
  background: #f5edff;
}

.cdoc-review-legend__item--legal,
.cdoc-review-legend__item--adjustment {
  border-color: #7aa7ff;
  background: #eef5ff;
}

.cdoc-review-legend__item--sensitive {
  border-color: #e68597;
  background: #fff0f3;
}

.cdoc-layout-panel {
  border-top: 1px solid #dfe9e6;
  background: #ffffff;
  padding: 12px 18px 14px;
}

.cdoc-layout-panel__header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
}

.cdoc-layout-panel__header strong {
  color: #101918;
  font-size: 14px;
  line-height: 1.2;
}

.cdoc-layout-panel__header span {
  color: #758681;
  font-size: 12px;
}

.cdoc-layout-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 12px;
  align-items: start;
}

.cdoc-layout-grid ::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.cdoc-layout-grid ::v-deep .v-input--selection-controls .v-input__slot {
  min-height: 30px;
}

.cdoc-layout-grid ::v-deep .v-text-field .v-input__slot {
  min-height: 38px !important;
}

.cdoc-layout-grid ::v-deep .v-label {
  font-size: 12px;
}

.cdoc-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
  padding: 24px;
}

.cdoc-shell--comments {
  grid-template-columns: minmax(0, 1fr) 300px;
}

.cdoc-stage {
  min-width: 0;
  overflow: auto;
  padding: 0;
  position: relative;
}

.cdoc-stage--annotating {
  cursor: crosshair;
}

.cdoc-page {
  max-width: 760px;
  min-height: 900px;
  margin: 0 auto;
  padding: 62px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 18px 45px rgba(16, 22, 35, .08);
  position: relative;
}

.cdoc-editable-page {
  min-height: 720px;
  color: #101623;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 15px;
  line-height: 1.75;
  outline: none;
  position: relative;
  white-space: pre-wrap;
  z-index: 1;
}

.cdoc-editable-page ::v-deep h2,
.cdoc-editable-page ::v-deep h3 {
  margin: 18px 0 10px;
  line-height: 1.3;
}

.cdoc-editable-page ::v-deep blockquote {
  margin: 16px 0;
  padding: 10px 16px;
  border-left: 3px solid #0f4efb;
  background: #f7f9ff;
}

.cdoc-editable-page ::v-deep .document-review-highlight {
  display: inline;
  padding: 2px 3px;
  border-radius: 3px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  color: inherit;
  transition: box-shadow .2s ease, outline-color .2s ease;
}

.cdoc-editable-page ::v-deep .document-review-highlight--placeholder {
  background: #fff0c2;
  box-shadow: inset 0 -1px 0 #d08a00;
}

.cdoc-editable-page ::v-deep .document-review-highlight--numeric {
  background: #f2e7ff;
  box-shadow: inset 0 -1px 0 #7e22ce;
}

.cdoc-editable-page ::v-deep .document-review-highlight--adjustment {
  background: #edf4ff;
  box-shadow: inset 0 -1px 0 #2563eb;
}

.cdoc-editable-page ::v-deep .document-review-highlight--legal {
  background: #e6f0ff;
  box-shadow: inset 0 -1px 0 #0f4efb;
}

.cdoc-editable-page ::v-deep .document-review-highlight--sensitive {
  background: #ffe4ea;
  box-shadow: inset 0 -1px 0 #b4233f;
}

.cdoc-editable-page ::v-deep .document-review-highlight--focused {
  outline: 2px solid #0f4efb;
  outline-offset: 2px;
  box-shadow: 0 0 0 5px rgba(15, 78, 251, .16), inset 0 -1px 0 currentColor;
}

.cdoc-cover {
  width: min(760px, 100%);
  min-height: 420px;
  margin: 0 auto 22px;
  padding: 72px;
  background: #fff;
  border: 1px solid #dfe9e6;
  box-shadow: 0 24px 60px -42px rgba(18, 42, 37, .34);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cdoc-cover span {
  color: #1457ff;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.cdoc-cover h1 {
  margin: 16px 0;
  font-size: 42px;
  line-height: 1.1;
}

.cdoc-page-header,
.cdoc-page-footer {
  color: #71817d;
  font-size: 12px;
  letter-spacing: .02em;
  position: relative;
  z-index: 1;
}

.cdoc-page-header {
  border-bottom: 1px solid #dfe9e6;
  margin-bottom: 24px;
  padding-bottom: 14px;
}

.cdoc-page-footer {
  border-top: 1px solid #dfe9e6;
  margin-top: 34px;
  padding-top: 14px;
}

.cdoc-watermark {
  color: rgba(20, 87, 255, .08);
  font-size: 76px;
  font-weight: 850;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  text-transform: uppercase;
  transform: rotate(-28deg);
  z-index: 0;
}

.cdoc-color-control {
  align-items: center;
  border: 1px solid #dfe9e6;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  gap: 6px;
  height: 38px;
  padding: 0 8px;
}

.cdoc-color-control input {
  border: 0;
  height: 24px;
  padding: 0;
  width: 28px;
}

.cdoc-floating-toolbar {
  position: fixed;
  z-index: 80;
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: calc(100vw - 24px);
  padding: 7px;
  border: 1px solid #d7e0dd;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(16, 22, 35, .16);
  color: #17211f;
}

.cdoc-floating-toolbar::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -6px;
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-right: 1px solid #d7e0dd;
  border-bottom: 1px solid #d7e0dd;
  transform: translateX(-50%) rotate(45deg);
}

.cdoc-floating-toolbar > button,
.cdoc-floating-toolbar label,
.cdoc-floating-select {
  position: relative;
  z-index: 1;
}

.cdoc-floating-toolbar > button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #17211f;
  cursor: pointer;
}

.cdoc-floating-toolbar > button:hover {
  background: #eef5ff;
  color: #0f4efb;
}

.cdoc-floating-toolbar > button ::v-deep .v-icon,
.cdoc-floating-toolbar label ::v-deep .v-icon {
  color: currentColor;
}

.cdoc-floating-select {
  position: relative;
}

.cdoc-floating-select__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 142px;
  height: 34px;
  padding: 0 9px;
  border: 1px solid #d7e0dd;
  border-radius: 7px;
  background: #f8fafc;
  color: #17211f;
  cursor: pointer;
}

.cdoc-floating-select--style .cdoc-floating-select__trigger {
  width: 112px;
}

.cdoc-floating-select__trigger span {
  min-width: 0;
  overflow: hidden;
  font-size: 12px;
  font-weight: 850;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cdoc-floating-select__trigger:hover {
  border-color: #0f4efb;
  background: #eef5ff;
  color: #0f4efb;
}

.cdoc-floating-select__trigger ::v-deep .v-icon {
  color: currentColor;
}

.cdoc-floating-select__menu {
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  z-index: 6;
  min-width: 100%;
  max-height: 232px;
  overflow: auto;
  padding: 6px;
  border: 1px solid #d7e0dd;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 38px rgba(16, 22, 35, .18);
}

.cdoc-floating-select__menu--fonts {
  width: 206px;
}

.cdoc-floating-select__menu button {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 32px;
  padding: 0 9px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #17211f;
  cursor: pointer;
  font-size: 12px;
  font-weight: 750;
  text-align: left;
}

.cdoc-floating-select__menu button:hover {
  background: #eef5ff;
  color: #0f4efb;
}

.cdoc-floating-select__menu button.active {
  background: #0f4efb;
  color: #ffffff;
}

.cdoc-floating-toolbar label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 32px;
  padding: 0 7px;
  border: 1px solid #d7e0dd;
  border-radius: 6px;
  background: #f8fafc;
  color: #17211f;
  cursor: pointer;
}

.cdoc-floating-toolbar label:hover {
  border-color: #0f4efb;
  background: #eef5ff;
  color: #0f4efb;
}

.cdoc-floating-toolbar input[type='color'] {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;
}

.cdoc-floating-toolbar__divider {
  width: 1px;
  height: 24px;
  margin: 0 4px;
  background: #d7e0dd;
}

.cdoc-comment-marker {
  position: absolute;
  z-index: 4;
  width: 24px;
  height: 24px;
  border: 2px solid #ffffff;
  border-radius: 999px;
  background: #e83e5a;
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
  line-height: 20px;
  text-align: center;
  transform: translate(-50%, -50%);
}

.cdoc-comment-marker.active {
  background: #0f4efb;
}

.cdoc-comments-panel {
  border: 1px solid #dde5e2;
  background: #ffffff;
  padding: 16px;
}

.cdoc-comments-panel h3 {
  margin: 0 0 8px;
}

.cdoc-comments-panel > p {
  margin: 0 0 12px;
  color: #667085;
  font-size: 12px;
}

.cdoc-comment-card {
  border: 1px solid #dde5e2;
  background: #ffffff;
  padding: 10px;
  cursor: pointer;
}

.cdoc-comment-card.active {
  border-color: #0f4efb;
  box-shadow: inset 0 0 0 1px #0f4efb;
}

.cdoc-comment-card + .cdoc-comment-card {
  margin-top: 8px;
}

.cdoc-comment-card p {
  margin: 6px 0 0;
  color: #34413f;
  font-size: 13px;
  line-height: 1.45;
}

.cdoc-empty-comments {
  padding: 18px;
  border: 1px dashed #dde5e2;
  color: #6b7280;
  font-size: 12px;
  text-align: center;
}

.cdoc-comment-composer {
  position: absolute;
  z-index: 7;
  width: min(320px, calc(100% - 32px));
  padding: 12px;
  border: 1px solid #d8e1df;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 22px 48px -28px rgba(16, 22, 35, .55);
  transform: translate(-50%, 14px);
}

.cdoc-comment-composer::before {
  content: '';
  position: absolute;
  top: -7px;
  left: 50%;
  width: 14px;
  height: 14px;
  border-top: 1px solid #d8e1df;
  border-left: 1px solid #d8e1df;
  background: #ffffff;
  transform: translateX(-50%) rotate(45deg);
}

.cdoc-comment-composer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.cdoc-comment-composer__header strong {
  color: #17211f;
  font-size: 13px;
  font-weight: 900;
}

.cdoc-comment-composer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 999px;
  background: #f3f6f5;
  color: #475467;
}

.cdoc-comment-composer__close:hover {
  background: #e8eeee;
}

.cdoc-comment-composer textarea {
  width: 100%;
  min-height: 92px;
  padding: 10px 12px;
  border: 1px solid #d8e1df;
  border-radius: 6px;
  background: #fbfcfc;
  color: #17211f;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
  outline: none;
}

.cdoc-comment-composer textarea:focus {
  border-color: #0f4efb;
  box-shadow: 0 0 0 3px rgba(15, 78, 251, .12);
}

.cdoc-comment-composer textarea::placeholder {
  color: #98a2b3;
}

.cdoc-comment-composer__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.cdoc-comment-composer__actions button {
  border: 1px solid #d8e1df;
  border-radius: 6px;
  background: #ffffff;
  color: #34413f;
  padding: 7px 11px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
}

.cdoc-comment-composer__actions button:hover {
  border-color: #b7c6c2;
  background: #f8faf9;
}

.cdoc-comment-composer__actions button.primary {
  background: #0f4efb;
  border-color: #0f4efb;
  color: #ffffff;
}

.cdoc-comment-composer__actions button.primary:hover {
  background: #0d43dc;
  border-color: #0d43dc;
}

@media (max-width: 980px) {
  .cdoc-header {
    grid-template-columns: 1fr;
  }

  .cdoc-status {
    justify-content: flex-start;
  }

  .cdoc-toolbar-select,
  .cdoc-toolbar-select--small,
  .cdoc-toolbar-select--tiny {
    flex: 1 1 150px;
    max-width: none;
  }

  .cdoc-layout-grid {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .cdoc-shell--comments {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .cdoc-toolbar {
    align-items: stretch;
  }

  .cdoc-toolbar-select,
  .cdoc-toolbar-select--small,
  .cdoc-toolbar-select--tiny {
    flex-basis: 100%;
  }

  .cdoc-layout-grid {
    grid-template-columns: 1fr;
  }

  .cdoc-cover {
    padding: 40px 28px;
  }

  .cdoc-page {
    padding: 36px 24px;
  }
}
</style>
